import React, { ComponentType, FC, ReactNode } from 'react';
import { NotFound, ServerError } from '@saucelabs/sl-component-error-pages';

import { ErrorPageWrapper } from './ErrorPageWrapper';

type ErrorPageProps = Parameters<typeof ServerError>[0];

/**
 * Displays the children by default, unless isError = true in which case it displays the error
 * children / fallback.
 *
 * @param props
 */
export const ContentErrorHandler: FC<ContentErrorHandlerProps> = (props) => {
  const {
    isError,
    children,
    errorChildren: ErrorChildren,
    isServerError,
    isNotFoundError,
    notFoundPageProps,
    serverErrorPageProps,
  } = props;

  if (isServerError) {
    return (
      <ErrorPageWrapper>
        <ServerError
          embedded
          {...serverErrorPageProps}
        />
      </ErrorPageWrapper>
    );
  }

  if (isNotFoundError) {
    return (
      <ErrorPageWrapper>
        <NotFound
          embedded
          {...notFoundPageProps}
        />
      </ErrorPageWrapper>
    );
  }

  if (isError) {
    if (typeof ErrorChildren === 'function') {
      return <ErrorChildren />;
    }
    return <>{ErrorChildren}</>;
  }

  return <>{children}</>;
};

export interface ContentErrorHandlerProps {
  /**
   * Whether there is a server-level error.
   */
  isServerError?: boolean;
  /**
   * Additional props to pass to the server error page. Can be used to customize the description,
   * error message, etc.
   */
  serverErrorPageProps?: ErrorPageProps;

  /**
   * Whether the requested resource is not found.
   */
  isNotFoundError?: boolean;
  /**
   * Additional props to pass to the not found error page. Can be used to customize the description,
   * error message, etc.
   */
  notFoundPageProps?: ErrorPageProps;

  /**
   * Used to display a custom / generic error message / children.
   */
  isError?: boolean;

  /**
   * The children to display when an error is present in isError = true.
   */
  errorChildren?: ComponentType | ReactNode;

  /**
   * The children to display by default.
   */
  children: ReactNode;
}
